/**
 * AdminSEOAdvancedCard — admin UI for Google Search Console verification +
 * IndexNow auto-sitemap submission.
 *
 *  • GSC meta-tag method: paste the `content=...` from Google's HTML tag.
 *    Frontend injects it via Helmet on every page (handled in App.js).
 *  • GSC HTML-file method: upload the filename + body Google gives you.
 *    Backend writes it to `/public/` so it's served at the site root.
 *  • IndexNow key: admin generates / pastes a hex key (8-40 chars). Backend
 *    hosts the corresponding `<key>.txt` and POSTs to api.indexnow.org +
 *    bing.com whenever the sitemap changes (admin save, new vendor signup,
 *    daily cron).
 *
 * Surfaces last-ping timestamp + URL count so admin can see freshness.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, Spinner, FloppyDisk, ArrowsClockwise, ShieldCheck, Broadcast } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const genIndexNowKey = () => {
  const hex = 'abcdef0123456789';
  let k = '';
  for (let i = 0; i < 32; i += 1) k += hex[Math.floor(Math.random() * 16)];
  return k;
};

const AdminSEOAdvancedCard = () => {
  const [loading, setLoading] = useState(true);
  const [savingGSC, setSavingGSC] = useState(false);
  const [savingIN, setSavingIN] = useState(false);
  const [pinging, setPinging] = useState(false);

  const [metaToken, setMetaToken] = useState('');
  const [htmlFilename, setHtmlFilename] = useState('');
  const [htmlContent, setHtmlContent] = useState('');

  const [inKey, setInKey] = useState('');
  const [inEnabled, setInEnabled] = useState(true);
  const [lastPing, setLastPing] = useState({ ts: null, count: null });

  const load = async () => {
    setLoading(true);
    try {
      const [gsc, idx] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/gsc`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/indexnow`, { withCredentials: true }),
      ]);
      setMetaToken(gsc.data.meta_token || '');
      setHtmlFilename(gsc.data.html_filename || '');
      setHtmlContent(gsc.data.html_content || '');
      setInKey(idx.data.key || '');
      setInEnabled(idx.data.enabled !== false);
      setLastPing({ ts: idx.data.last_ping_at, count: idx.data.last_ping_count });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load SEO advanced settings');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveGSC = async () => {
    setSavingGSC(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/gsc`, {
        meta_token: metaToken,
        html_filename: htmlFilename,
        html_content: htmlContent,
      }, { withCredentials: true });
      toast.success('Google Search Console settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save GSC');
    }
    setSavingGSC(false);
  };

  const saveIndexNow = async () => {
    setSavingIN(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/indexnow`, {
        key: inKey, enabled: inEnabled,
      }, { withCredentials: true });
      setInKey(data.key || '');
      toast.success('IndexNow settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save IndexNow');
    }
    setSavingIN(false);
  };

  const runPing = async () => {
    setPinging(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/seo-ping`, {}, { withCredentials: true });
      if (data.skipped) {
        toast.warning(`Ping skipped: ${data.reason}`);
      } else {
        toast.success(`Submitted ${data.url_count} URLs to IndexNow + Google`);
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ping failed');
    }
    setPinging(false);
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-seo-advanced-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Globe size={20} className="text-brand-primary" weight="fill" /> SEO Verification & Sitemap Submission
        </CardTitle>
        <CardDescription>
          Verify ownership with Google Search Console and auto-submit fresh URLs to IndexNow
          (Bing / Yandex / Naver) every time the sitemap changes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-text-secondary py-4 text-center">Loading…</p>
        ) : (
          <div className="space-y-6">
            {/* GSC */}
            <div className="border border-[#E8E1D5] p-4 bg-sand-50">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} className="text-brand-primary" weight="fill" />
                <span className="text-sm font-bold">Google Search Console verification</span>
              </div>
              <p className="text-[11px] text-text-secondary mb-3">
                Provide <strong>either</strong> the meta-tag value <strong>or</strong> the HTML-file (or both).
                The meta-tag is injected on every page; the HTML file is served at the site root.
              </p>

              <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
                Meta-tag content (just the value between content="…")
              </label>
              <Input value={metaToken}
                onChange={(e) => setMetaToken(e.target.value)}
                placeholder='e.g. "abc123XYZdef456_googleVerifyValue"'
                className="h-9 rounded-none border-[#E8E1D5] mt-1"
                data-testid="admin-gsc-meta-input" />

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
                    HTML file name (google&lt;hex&gt;.html)
                  </label>
                  <Input value={htmlFilename}
                    onChange={(e) => setHtmlFilename(e.target.value)}
                    placeholder="e.g. google1234567890abcdef.html"
                    className="h-9 rounded-none border-[#E8E1D5] mt-1"
                    data-testid="admin-gsc-htmlfile-input" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
                    HTML file body (paste from Google)
                  </label>
                  <Textarea value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    rows={2}
                    className="rounded-none border-[#E8E1D5] mt-1 text-xs font-mono"
                    placeholder="google-site-verification: googleabc.html"
                    data-testid="admin-gsc-htmlbody-input" />
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E1D5]">
                <span className="text-[11px] text-text-secondary">
                  {htmlFilename ? (
                    <>File will be served at <a href={`/${htmlFilename}`} target="_blank" rel="noreferrer"
                      className="text-brand-primary hover:underline">/{htmlFilename}</a></>
                  ) : 'No HTML file configured.'}
                </span>
                <Button onClick={saveGSC} disabled={savingGSC}
                  className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 h-9"
                  data-testid="admin-gsc-save-btn">
                  {savingGSC ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
                  Save GSC
                </Button>
              </div>
            </div>

            {/* IndexNow */}
            <div className="border border-[#E8E1D5] p-4 bg-sand-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Broadcast size={16} className="text-brand-primary" weight="fill" />
                  <span className="text-sm font-bold">IndexNow auto-sitemap submission</span>
                </div>
                <Switch checked={inEnabled} onCheckedChange={setInEnabled}
                  data-testid="admin-indexnow-enabled-switch" />
              </div>
              <p className="text-[11px] text-text-secondary mb-3">
                When enabled, the platform submits fresh URLs to <code className="font-mono">api.indexnow.org</code> +
                Bing whenever the SEO cities list changes, a vendor / professional signs up, or once daily.
                Google's deprecated ping endpoint is also hit best-effort.
              </p>

              <label className="text-[11px] uppercase tracking-wider font-bold text-text-secondary">
                IndexNow key (8-40 hex characters)
              </label>
              <div className="flex gap-2 mt-1">
                <Input value={inKey}
                  onChange={(e) => setInKey(e.target.value)}
                  placeholder="e.g. abc123def456abc123def456abc123de"
                  className="h-9 rounded-none border-[#E8E1D5] font-mono flex-1"
                  data-testid="admin-indexnow-key-input" />
                <Button onClick={() => setInKey(genIndexNowKey())}
                  variant="outline"
                  className="rounded-none border-[#E8E1D5] h-9"
                  data-testid="admin-indexnow-gen-key-btn">
                  Generate
                </Button>
              </div>
              <p className="text-[10px] text-text-secondary mt-1">
                Backend will host <span className="font-mono">/{inKey || '<key>'}.txt</span> so IndexNow can verify ownership.
              </p>

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E8E1D5]">
                <div className="text-[11px] text-text-secondary">
                  {lastPing.ts ? (
                    <>Last ping: <strong>{new Date(lastPing.ts).toLocaleString()}</strong> ·{' '}
                      <strong>{lastPing.count}</strong> URLs submitted</>
                  ) : 'No pings sent yet.'}
                </div>
                <div className="flex gap-2">
                  <Button onClick={runPing} disabled={pinging || !inKey} variant="outline"
                    className="rounded-none border-[#E8E1D5] h-9"
                    data-testid="admin-indexnow-ping-btn"
                    title={!inKey ? 'Add a key first' : ''}>
                    {pinging ? <Spinner size={14} className="mr-2 animate-spin" /> : <ArrowsClockwise size={14} className="mr-2" weight="bold" />}
                    Ping now
                  </Button>
                  <Button onClick={saveIndexNow} disabled={savingIN}
                    className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 h-9"
                    data-testid="admin-indexnow-save-btn">
                    {savingIN ? <Spinner size={14} className="mr-2 animate-spin" /> : <FloppyDisk size={14} className="mr-2" weight="fill" />}
                    Save IndexNow
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminSEOAdvancedCard;
